import React from "react";
import * as XLSX from "xlsx";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TableContainer,
  Button,
  Grid,
  TablePagination,
  InputAdornment,
  TextField,
  Divider,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import PageContainer from "src/components/container/PageContainer";
import ParentCard from "src/components/shared/ParentCard";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import swal from "sweetalert";
import { Link } from "react-router-dom";
import { IconFileDownload, IconPlus, IconSearch } from "@tabler/icons";
import Breadcrumb from "src/layouts/full/shared/breadcrumb/Breadcrumb";
import { writeFile } from "xlsx";
import BulkImeiService from "src/service/bulkdeviceadd";
import GeofencingService from "src/service/geofencing";
import CustomFormLabel from "src/components/forms/theme-elements/CustomFormLabel";
import CustomTextField from "src/components/forms/theme-elements/CustomTextField";

const BCrumb = [
  {
    to: "/",
    title: "Home",
  },
  {
    title: "Vehicle",
  },
];

const BulkVehicleList = () => {
  // ====================================

  const navigate = useNavigate();

  const sessionData = JSON.parse(localStorage.getItem("user"));
  const [vehicle_listing, set_vehicle_list] = useState([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [visibleData, setVisibleData] = useState([]);

  //  for table sorting
  const [sortOrder, setSortOrder] = useState("asc");
  const [sortColumn, setSortColumn] = useState("vehicle_name");

  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [selectedVehicleType, setSelectedVehicleType] = useState("");

  const fetch_vehicle_data = async (district_id, vehicle_type) => {
    try {
      const res = await BulkImeiService.list_bulk_vehicle_data({
        district_id: district_id,
        vehicle_type: vehicle_type,
      });

      set_vehicle_list(res.data);
      setVisibleData(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  const delete_vehicle_data = async (vehicle_no) => {
    swal({
      title: "Delete Vehicle",
      text: "Are you sure you want to delete this Vehicle?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          const response = await BulkImeiService.delete_bulk_vehicle_data({
            vehicle_no: vehicle_no,
            d_by: sessionData.data.id,
          });
          if (response.status === 200) {
            swal("success", response.message, "success");
            fetch_vehicle_data();
          } else {
            swal("Error", response.message, "error");
          }
        } catch (error) {
          swal("Error", error, "error");
        }
      }
    });
  };

  useEffect(() => {
    fetch_vehicle_data(selectedDistrict, selectedVehicleType);
  }, [selectedDistrict, selectedVehicleType]);

  const filter_list = vehicle_listing.filter((man) => {
    const vehicleTypeDisplay =
      man.vehicle_type?.toLowerCase() === "e"
        ? "ERV"
        : man.vehicle_type?.toLowerCase() === "f"
        ? "Fire Brigade"
        : "";

    return (
      (man.vehicle_no?.toLowerCase().includes(searchTerm.toLowerCase()) ??
        false) ||
      (man.erv_no?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false) ||
      (man.district_name?.toLowerCase().includes(searchTerm.toLowerCase()) ??
        false) ||
      (vehicleTypeDisplay.toLowerCase().includes(searchTerm.toLowerCase()) ??
        false) ||
      (man.imei_no?.includes(searchTerm) ?? false)
    );
  });

  useEffect(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    const filteredData = vehicle_listing.filter((man) =>
      (man.vehicle_name?.toLowerCase() ?? "").includes(searchTerm.toLowerCase())
    );
    const visibleData = filteredData.slice(startIndex, endIndex);
    setVisibleData(visibleData);
  }, [vehicle_listing, page, rowsPerPage, searchTerm]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = () => {
    const headers = ["Sl. No", "Device Name", "Imei No"];

    const data = visibleData.map((man, index) => [
      index + 1,
      man.vehicle_name,
      man.imei_no,
    ]);

    const exportData = [headers, ...data];

    const ws = XLSX.utils.aoa_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet 1");

    const fileName = "device_data.xlsx";

    writeFile(wb, fileName);
  };

  const download_excel_file = () => {
    const headers = ["VehicleNo", "ERVNo", "ImeiNo", "District", "VehicleType"];

    const data = visibleData.map((man, index) => []);

    const exportData = [headers, ...data];

    const ws = XLSX.utils.aoa_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet 1");

    const fileName = "vehicle_data.xlsx";

    writeFile(wb, fileName);
  };

  // table column sorting start here
  const handleSort = (column) => {
    // Toggle sorting order if clicking on the same column
    if (sortColumn === column) {
      setSortOrder((prevSortOrder) =>
        prevSortOrder === "asc" ? "desc" : "asc"
      );
    } else {
      // Set new column to sort
      setSortColumn(column);
      // Default sorting order for the new column
      setSortOrder("asc");
    }
  };

  const sortedVehicle = filter_list.sort((a, b) => {
    const compareValueA = a[sortColumn];
    const compareValueB = b[sortColumn];

    // Handle null or undefined values
    if (compareValueA == null && compareValueB == null) {
      return 0;
    } else if (compareValueA == null) {
      return sortOrder === "asc" ? -1 : 1;
    } else if (compareValueB == null) {
      return sortOrder === "asc" ? 1 : -1;
    }

    // Customize the comparison based on data types or specific conditions
    if (sortOrder === "asc") {
      return compareValueA.localeCompare(compareValueB);
    } else {
      return compareValueB.localeCompare(compareValueA);
    }
  });

  const [districtList, setDistrictList] = useState([]);
  const fetchDistrictList = async () => {
    try {
      const res = await GeofencingService.fetch_district_list({});
      setDistrictList([{ id: "", district_name: "All" }, ...res.data]);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchDistrictList();
  }, []);

  const vehicle_type_list = [
    { value: "", label: "All" },
    { value: "e", label: "ERV" },
    { value: "f", label: "Fire Brigade" },
  ];

  // ====================================
  return (
    <PageContainer title="Vehicle" description="">
      {/* breadcrumb */}
      <Breadcrumb title="Vehicle" items={BCrumb} />
      {/* end breadcrumb */}

      <ParentCard title="">
        <Grid container alignItems="center">
          <Grid
            item
            xs={4}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              gap: 2,
              marginBottom: "15px;",
              marginTop: "15px;",
            }}
          >
            <Typography
              style={{
                fontSize: "22px",
                fontWeight: "bold",
                fontFamily: "Sans-Serif",
              }}
              variant="h2"
            >
              Vehicle List
            </Typography>
          </Grid>

          <Grid
            item
            xs={8}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              marginBottom: "15px;",
              marginTop: "15px;",
            }}
          >
            <Tooltip title="Download Format">
              <Button
                variant="contained"
                color="info"
                onClick={download_excel_file}
              >
                <IconFileDownload width={20} />
              </Button>
            </Tooltip>
            {sessionData.data.user_type === 1 && (
              <Tooltip title="Upload Data">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    navigate("/addbulkvehicle");
                  }}
                >
                  <IconPlus width={20} />
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Download Report">
              <Button
                variant="contained"
                color="success"
                onClick={handleExport}
              >
                <IconFileDownload width={20} />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
        <Divider />
        <Grid container spacing={3}>
          <Grid item lg={4} md={4} sm={6} xs={12}>
            <CustomFormLabel htmlFor="district_code">District</CustomFormLabel>
            <Autocomplete
              id="district_code"
              name="district_code"
              options={districtList}
              getOptionLabel={(option) => option.district_name}
              onChange={(event, newValue) => {
                setSelectedDistrict(newValue ? newValue.id : "");
              }}
              renderInput={(params) => (
                <CustomTextField
                  {...params}
                  fullWidth
                  variant="outlined"
                  placeholder="Search or select a District"
                />
              )}
            />
          </Grid>

          <Grid item lg={4} md={4} sm={6} xs={12}>
            <CustomFormLabel>Vehicle Type</CustomFormLabel>
            <Autocomplete
              id="vehicle_type"
              name="vehicle_type"
              options={vehicle_type_list}
              getOptionLabel={(option) => option.label}
              value={
                vehicle_type_list.find(
                  (option) => option.value === selectedVehicleType
                ) || null
              }
              onChange={(event, newValue) =>
                setSelectedVehicleType(newValue ? newValue.value : "")
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select Vehicle Type"
                  fullWidth
                />
              )}
            />
          </Grid>

          <Grid
            item
            lg={4}
            md={4}
            sm={6}
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              marginBottom: "20px;",
              marginTop: "30px;",
            }}
          >
            <TextField
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconSearch size="1.1rem" />
                  </InputAdornment>
                ),
              }}
              placeholder="Search here "
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
        </Grid>
        {/* </ParentCard> */}

        <Paper variant="outlined">
          <TableContainer>
            <Table
              aria-label="simple table"
              sx={{
                whiteSpace: "nowrap",
              }}
            >
              <TableHead style={{ backgroundColor: "#895c5c33" }}>
                <TableRow>
                  <TableCell>
                    <Typography
                      style={{ fontSize: "14px", textAlign: "center" }}
                      variant="h6"
                    >
                      Sl. No
                    </Typography>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("vehicle_no")}
                    style={{ cursor: "pointer" }}
                  >
                    <Typography
                      style={{ fontSize: "14px", textAlign: "center" }}
                      variant="h6"
                    >
                      Vehicle No
                      {/* Add arrow icon based on sorting order and column */}
                      {sortColumn === "vehicle_no" &&
                        (sortOrder === "asc" ? " ▲" : " ▼")}
                    </Typography>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("erv_no")}
                    style={{ cursor: "pointer" }}
                  >
                    <Typography
                      style={{ fontSize: "14px", textAlign: "center" }}
                      variant="h6"
                    >
                      ERV No
                      {/* Add arrow icon based on sorting order and column */}
                      {sortColumn === "erv_no" &&
                        (sortOrder === "asc" ? " ▲" : " ▼")}
                    </Typography>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("imei_no")}
                    style={{ cursor: "pointer" }}
                  >
                    <Typography
                      style={{ fontSize: "14px", textAlign: "center" }}
                      variant="h6"
                    >
                      Imei No
                      {/* Add arrow icon based on sorting order and column */}
                      {sortColumn === "imei_no" &&
                        (sortOrder === "asc" ? " ▲" : " ▼")}
                    </Typography>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("district")}
                    style={{ cursor: "pointer" }}
                  >
                    <Typography
                      style={{ fontSize: "14px", textAlign: "center" }}
                      variant="h6"
                    >
                      District
                      {/* Add arrow icon based on sorting order and column */}
                      {sortColumn === "district" &&
                        (sortOrder === "asc" ? " ▲" : " ▼")}
                    </Typography>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort("vehicle_type")}
                    style={{ cursor: "pointer" }}
                  >
                    <Typography
                      style={{ fontSize: "14px", textAlign: "center" }}
                      variant="h6"
                    >
                      Vehicle Type
                      {/* Add arrow icon based on sorting order and column */}
                      {sortColumn === "vehicle_type" &&
                        (sortOrder === "asc" ? " ▲" : " ▼")}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    {sessionData.data.user_type === 1 && (
                      <>
                        <Typography
                          style={{ fontSize: "15px", textAlign: "center" }}
                          variant="h6"
                        >
                          Action
                        </Typography>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedVehicle.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} style={{ textAlign: "center" }}>
                      <Typography
                        style={{ fontSize: "13px" }}
                        variant="h6"
                        sx={{ color: "red" }}
                      >
                        No records found!!
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedVehicle
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((man, index) => (
                      <TableRow key={man.id}>
                        <TableCell style={{ textAlign: "center" }}>
                          <Typography style={{ fontSize: "14px" }} variant="h6">
                            {index + 1}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <Typography style={{ fontSize: "14px" }} variant="h6">
                            {man.vehicle_no ?? "NA"}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <Typography style={{ fontSize: "14px" }} variant="h6">
                            {man?.erv_no ?? "NA"}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <Typography style={{ fontSize: "14px" }} variant="h6">
                            {man?.imei_no ?? "NA"}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <Typography style={{ fontSize: "14px" }} variant="h6">
                            {man?.district_name ?? "NA"}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ textAlign: "center" }}>
                          <Typography style={{ fontSize: "14px" }} variant="h6">
                            {man?.vehicle_type?.toLowerCase() === "e"
                              ? "ERV"
                              : man?.vehicle_type?.toLowerCase() === "f"
                              ? "Fire Brigade"
                              : "NA"}
                          </Typography>
                        </TableCell>

                        <TableCell style={{ textAlign: "center" }}>
                          {sessionData.data.user_type === 1 && (
                            <>
                              {/* <Link
                                style={{ marginLeft: "14%" }}
                                to={`/updatebulkvehicle/${man.id}`}
                              >
                                <ModeEditOutlinedIcon
                                  style={{
                                    color: "blue",
                                    fontSize: "18px",
                                    cursor: "pointer",
                                  }}
                                />
                              </Link> */}
                              <DeleteOutlinedIcon
                                style={{
                                  color: "red",
                                  fontSize: "18px",
                                  marginLeft: "10px",
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  delete_vehicle_data(man.vehicle_no)
                                }
                              />
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
          component="div"
          count={sortedVehicle.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </ParentCard>
    </PageContainer>
  );
};

export default BulkVehicleList;
